import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { Tab } from '@affinis/smartus-components/lib/tabs/tabs.components.types';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  ArchObjHierarchyViewModel,
  ArchObjLocationInformationViewModel,
  BaseSearchOptions,
  IncidentClient,
  LocationClient,
  LocationSearchEntryViewModel,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { DeviceService } from '@core/services/device-service/device.service';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { UserService } from '@core/services/oauth-service/user.service';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import {
  ITableConfig,
  ITableGridConfig,
  ITablePagingConfig,
} from '@shared/components/table-base/table-base.types';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { rxSubscriptionContainerMixin } from '@shared/utils/rx-subscription-container.mixin';
import { ToastrService } from 'ngx-toastr';
import { LocationInformationEditComponent } from '../components';
import { LocationInfoCategory } from '../location.constants';

@Component({
  selector: 'app-my-location',
  templateUrl: './my-location.component.html',
  styleUrls: ['./my-location.component.scss'],
  standalone: false,
})
export class MyLocationComponent
  extends rxSubscriptionContainerMixin()
  implements OnInit
{
  config: ITableConfig = <ITableConfig>{
    grid: <ITableGridConfig>{
      showToolbar: true,
      columnResizable: false,
      paging: <ITablePagingConfig>{
        pageIndex: 0,
        pageSize: 50,
        showPaginator: true,
      },
    },
  };

  query: BaseSearchOptions = {
    pagingOptions: {
      skip:
        this.config.grid.paging.pageIndex * this.config.grid.paging.pageSize,
      take: this.config.grid.paging.pageSize,
    },
  };

  form = new FormGroup({});
  formFields: Array<FormlyFieldConfig>;
  loading = false;
  archObjId = '';
  isLoading = false;
  loadedInformation = false;
  editLocationInfoTitle = this.translateService.instant(
    'location.myLocation.location_information.title'
  );
  LocationInfoCategory = LocationInfoCategory;
  locationInfos = [];

  locationInfosHints = [];
  locationInfosServices = [];
  locationInfosAdditionalServices = [];
  locationInfosUnpublished = [];

  public locationCategories = [];

  formModel = {};

  public selectedLocation: ArchObjHierarchyViewModel = null;

  cardSearch: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'location.myLocation.label__search_location'
        ),
      },
    },
    footer: {
      button2: {
        label: this.translateService.instant(
          'location.myLocation.favorites.create'
        ),
        tooltip: this.translateService.instant(
          'location.myLocation.favorites.create_tooltip'
        ),
        onClick: () => {
          this.toggleFavorite();
          this.cardSearch.footer.button2.disabled = true;
        },
        disabled: true,
        type: 'button',
      },
      button1: {
        label: this.translateService.instant(
          'location.myLocation.label_open_second_tab'
        ),
        onClick: () => {
          this.activeTab = this.tabs[1].id;
        },
        disabled: true,
        type: 'button',
      },
    },
  };

  cardMap: CardConfig = {
    header: {
      title: {
        label: 'Details',
      },
    },
  };

  cardHints: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'location.myLocation.location_information.hints'
        ),
      },
    },
  };

  cardServices: CardConfig = {
    header: {
      title: {
        label: 'Services',
      },
    },
  };

  cardAdditionalServices: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'location.myLocation.location_information.additional_services'
        ),
      },
    },
  };

  cardUnpublishedInfos: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant(
          'location.myLocation.location_information.unpublished_infos'
        ),
      },
    },
  };

  tabs: Tab[] = [
    {
      label: this.translateService.instant(
        'location.myLocation.favorites.title'
      ),
      id: 'suche',
      tooltip: this.translateService.instant(
        'location.myLocation.favorites.title_tooltip'
      ),
      onClick: (id: string) => this.onTabClick(id),
    },
    {
      label: this.translateService.instant(
        'location.myLocation.label__location_information'
      ),
      id: 'standort_information',
      tooltip: this.translateService.instant(
        'location.myLocation.label__location_information_tooltip'
      ),
      onClick: (id: string) => this.onTabClick(id),
      disabled: true,
    },
  ];

  activeTab = this.tabs[0].id;

  onTabClick(value: string) {
    this.activeTab = value;
  }

  @ViewChild('focusableCellTemplate', { static: true })
  focusableCellTemplateRef!: TemplateRef<void>;

  @ViewChild('favoriteIconTemplate', { static: true })
  favoriteIconTemplateRef!: TemplateRef<void>;

  disableFavoriteIcon = false;

  columnsFavorites: MtxGridColumn[] = [];

  headline = this.translateService.instant(
    'location.myLocation.label__myLocation'
  );

  isDataManager = false;

  isBuildingFaultButtonDisabled = true;

  public locationFavorites: LocationSearchEntryViewModel[];

  public isMobile: boolean;

  headerLevel = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private fieldService: LocationFormlyFieldService,
    private locationClient: LocationClient,
    private breadcrumbService: BreadcrumbService,
    private incidentClient: IncidentClient,
    private dialog: MatDialog,
    private userService: UserService,
    private toastr: ToastrService,
    private deviceService: DeviceService
  ) {
    super();
    this.getFavorites();

    this.deviceService.isMobile$.subscribe({
      next: res => {
        this.isMobile = res;
      },
    });

    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);
    this.locationClient.getLocationInfoCategories().subscribe({
      next: res => {
        this.locationCategories = res;
      },
    });
    this.pushSubscription(
      this.userService
        .checkIfUserHasRole(UserRoles.SMARTPORTAL_LOCATION_DATA_MANAGER)
        .subscribe(hasRole => (this.isDataManager = hasRole))
    );
    this.pushSubscription(
      this.userService
        .checkIfUserHasRole(UserRoles.SMARTPORTAL_LOCATION_MAIL)
        .subscribe(hasRole => {
          if (hasRole) {
            this.tabs.push({
              label: this.translateService.instant(
                'location.myLocation.label__email_employee'
              ),
              tooltip: this.translateService.instant(
                'location.myLocation.label__email_employee_tooltip'
              ),
              id: 'email_employee',
              onClick: (id: string) => this.onTabClick(id),
              disabled: this.selectedLocation ? false : true,
            });
          } else if (this.tabs.find(tab => tab.id === 'email_employee')) {
            this.tabs = this.tabs.filter(tab => tab.id !== 'email_employee');
          }
        })
    );
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.getHeaderLevel();
    }, 0);

    this.route.params.subscribe(params => {
      const locationId = params['id'];
      if (locationId) {
        this.onSelect(locationId, true);

        setTimeout(() => {
          this.activeTab = this.tabs[1].id;
        }, 100);
      } else {
        this.initializeFormFields();
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.loadedInformation = false;
        this.activeTab = this.tabs[0].id;
        if (this.selectedLocation) {
          this.onSelect(this.selectedLocation.dtArchObjAddress[0].archObjId);
        }
      }
    });

    this.columnsFavorites = [
      {
        header: this.translateService.instant(
          'location.myLocation.favorites.WE-No'
        ),
        field: 'id',
        cellTemplate: this.focusableCellTemplateRef,
      },
      {
        header: this.translateService.instant('location.autocomplete.zip'),
        field: 'postalCode',
        cellTemplate: this.focusableCellTemplateRef,
      },
      {
        header: this.translateService.instant('location.autocomplete.city'),
        field: 'city',
        cellTemplate: this.focusableCellTemplateRef,
      },
      {
        header: this.translateService.instant('location.autocomplete.street'),
        field: 'streetName',
        cellTemplate: this.focusableCellTemplateRef,
      },
      {
        header: this.translateService.instant(
          'location.autocomplete.housenumber'
        ),
        field: 'houseNumber',
        cellTemplate: this.focusableCellTemplateRef,
      },
      {
        header: this.translateService.instant(
          'location.myLocation.favorites.favorite'
        ),
        field: 'fav',
        cellTemplate: this.favoriteIconTemplateRef,
      },
    ];
  }

  private initializeFormFields() {
    this.formFields = [
      ...this.fieldService.autocompleteSearch(
        false,
        this.selectedLocation ?? null,
        true,
        (id, onInitSearch) => this.onSelect(id, onInitSearch),
        null,
        () => this.onDelete(),
        this.translateService.instant(
          'location.shared.delete_location_search_tooltip'
        )
      ),
    ];
  }

  private getHeaderLevel() {
    for (let i = 1; i <= 6; i++) {
      if (!document.querySelector(`h${i}`)) {
        this.headerLevel = i;
        break;
      }
    }
  }

  onDelete(): () => void {
    this.selectedLocation = null;
    this.isBuildingFaultButtonDisabled = true;
    this.tabs[1].disabled = true;
    this.cardSearch.footer.button1.disabled = true;
    this.cardSearch.footer.button2.disabled = true;
    if (this.tabs[2]) {
      this.tabs[2].disabled = true;
    }
    return null;
  }

  public get addressString() {
    if (
      !this.selectedLocation?.dtArchObjAddress ||
      !this.selectedLocation?.dtArchObjAddress[0].address
    ) {
      return '';
    }

    const address = this.selectedLocation?.dtArchObjAddress[0].address;
    return `${address.streetName}, ${address.postalCode} ${address.city}`;
  }

  public get addressCoordinates(): [number, number] {
    if (
      !this.selectedLocation?.dtArchObjAddress ||
      !this.selectedLocation?.dtArchObjAddress[0].address
    ) {
      return [0, 0];
    }

    const address = this.selectedLocation?.dtArchObjAddress[0].address;
    return [address.longitude, address.latitude];
  }

  private handleError(error: Error) {
    this.isLoading = false;
    console.error('[MyLocationComponent] handleError:', error);
  }

  public onSelect(archObjId: string, onInitSearch?: boolean): void {
    this.isLoading = true;
    this.locationClient.getArchobjById(archObjId).subscribe({
      next: (response: ArchObjHierarchyViewModel) => {
        this.selectedLocation = response;

        if (!this.formFields || this.archObjId !== archObjId) {
          this.initializeFormFields();
        }

        this.tabs[1].disabled = false;
        this.cardSearch.footer.button1.disabled = false;
        if (response.isFavorite === false) {
          this.cardSearch.footer.button2.disabled = false;
        }
        if (this.tabs[2]) {
          this.tabs[2].disabled = false;
        }
        this.isBuildingFaultButtonDisabled = false;
        if (onInitSearch === undefined) {
          this.activeTab = this.tabs[1].id;
          this.cardSearch.footer.button1.disabled = true;
          this.cardSearch.footer.button2.disabled = true;
        }
      },
      error: (error: any) => this.handleError(error),
      complete: () => {
        this.isLoading = false;
        if (
          window.location.hash === '#information' &&
          !this.loadedInformation
        ) {
          this.activeTab = this.tabs[0].id;
          this.loadedInformation = true;
        }
      },
    });
    this.archObjId = archObjId;

    this.getLocationList();
  }

  public editLocationInformation() {
    const locationInfo: ArchObjLocationInformationViewModel = {
      archObjId: this.selectedLocation.dtArchObjAddress[0].archObjId,
    };

    this.openEditDialog(locationInfo, false);
  }

  openEditDialog(item: ArchObjLocationInformationViewModel, isUpdate: boolean) {
    const editDialogRef = this.dialog.open(LocationInformationEditComponent, {
      minWidth: '40vw',
      minHeight: '60vh',
      data: {
        locationInfo: item,
        title: this.editLocationInfoTitle,
        update: isUpdate,
        categories: this.locationCategories,
        locationInfos: this.locationInfos,
      },
    });

    editDialogRef.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.getLocationList();
        }
      },
    });
  }

  public deleteLocationInformation(id?: string) {
    const dialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'location.myLocation.location_information.delete_dialog.title'
        ),
        message: this.translateService.instant(
          'location.myLocation.location_information.delete_dialog.message'
        ),
        okButtonText: this.translateService.instant('shared.confirm'),
        dismissButtonText: this.translateService.instant('shared.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result) {
          this.isLoading = true;
          this.locationClient.deleteLocationInformation(id).subscribe({
            next: () => this.getLocationList(),
            error: error => this.handleError(error),
          });
        }
      },
    });
  }

  public getLocationList() {
    this.isLoading = true;
    this.locationInfosHints = [];
    this.locationInfosServices = [];
    this.locationInfosAdditionalServices = [];
    this.locationInfosUnpublished = [];
    this.locationClient.getLocationInfo(this.archObjId).subscribe({
      next: res => {
        this.locationInfos = res;
        this.locationInfos.forEach(item => {
          const today = new Date();
          const itemValidFrom = new Date(item.validFrom);
          if (
            !item.isPublished ||
            (item.category.id === LocationInfoCategory.LocationHint &&
              itemValidFrom > today)
          ) {
            this.locationInfosUnpublished.push(item);
            return;
          }

          item.text = item.text.replace(/\n/g, '<br>');

          switch (item.category.id) {
            case LocationInfoCategory.LocationHint:
              this.locationInfosHints.push(item);
              break;
            case LocationInfoCategory.AdditionalServices:
              this.locationInfosAdditionalServices.push(item);
              break;
            default:
              this.locationInfosServices.push(item);
              break;
          }
        });
        this.isLoading = false;
      },
      error: error => this.handleError(error),
    });
  }

  public createBuildingFault() {
    if (!this.selectedLocation) {
      return;
    }
    const url = `create/helpdesk?archObjId=${this.selectedLocation.id}`;
    this.router.navigateByUrl(url);
  }

  public deleteFavorite(
    event?: MouseEvent,
    row?: LocationSearchEntryViewModel
  ) {
    event.stopPropagation();

    const deleteFavoriteDialogRef = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'location.myLocation.favorites.delete'
        ),
        message: this.translateService.instant(
          'location.myLocation.favorites.delete_message'
        ),
        okButtonText: this.translateService.instant('shared.confirm'),
        dismissButtonText: this.translateService.instant('shared.cancel'),
      },
    });

    deleteFavoriteDialogRef.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.locationClient.deleteFavorite(row.id).subscribe({
            next: () => {
              this.getFavorites();
              if (row.id === this.selectedLocation.id) {
                this.selectedLocation.isFavorite =
                  !this.selectedLocation.isFavorite;
                this.disableFavoriteIcon = false;
                this.cardSearch.footer.button2.disabled = false;
              }
              this.toastr.success(
                this.translateService.instant(
                  'location.myLocation.favorites.delete_success'
                )
              );
            },
            error: () => {
              this.toastr.success(
                this.translateService.instant(
                  'location.myLocation.favorites.delete_error'
                )
              );
            },
          });
        }
      },
    });
  }

  public toggleFavorite() {
    this.disableFavoriteIcon = true;
    if (this.selectedLocation.isFavorite) {
      this.locationClient.deleteFavorite(this.selectedLocation?.id).subscribe({
        next: () => {
          this.getFavorites();
          this.selectedLocation.isFavorite = !this.selectedLocation.isFavorite;
          this.disableFavoriteIcon = false;
          this.cardSearch.footer.button2.disabled = false;
        },
      });
    } else {
      this.locationClient.createFavorite(this.selectedLocation?.id).subscribe({
        next: () => {
          this.getFavorites();
          this.selectedLocation.isFavorite = !this.selectedLocation.isFavorite;
          this.disableFavoriteIcon = false;
          this.cardSearch.footer.button2.disabled = true;
        },
      });
    }
  }

  public getFavorites() {
    this.locationClient.getFavorites().subscribe({
      next: (favorites: any) => {
        this.locationFavorites = favorites;
        this.fieldService.getDefaultList();
      },
    });
  }
}
